import { useState, useEffect, useRef } from 'react'
import { finalThumbnailGenerator } from '../lib/finalThumbnailGenerator'
import type { Entry } from '../lib/types'

interface ThumbnailCardProps {
  entry: Entry
  path: string
  onClick: () => void
}

export function ThumbnailCard({ entry, path, onClick }: ThumbnailCardProps) {
  const [thumbnail, setThumbnail] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  
  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { 
        rootMargin: '100px',
        threshold: 0.01
      }
    )
    
    if (cardRef.current) {
      observer.observe(cardRef.current)
    }
    
    return () => observer.disconnect()
  }, [])
  
  // Load thumbnail when visible
  useEffect(() => {
    if (!isVisible) return
    
    let cancelled = false
    
    async function loadThumbnail() {
      try {
        setLoading(true)
        
        // Generate thumbnail with final optimized rendering
        const dataUrl = await finalThumbnailGenerator.generateThumbnail(
          path,
          {
            width: 440,  // 2x resolution
            height: 320, // 2x resolution
            quality: 0.92,
            allowLocal: import.meta.env.VITE_DISABLE_LOCAL !== 'true'
          }
        )
        
        if (!cancelled) {
          setThumbnail(dataUrl)
          setError(null)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Thumbnail generation error:', err)
          setError(err instanceof Error ? err.message : 'Failed to generate thumbnail')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    
    loadThumbnail()
    
    return () => {
      cancelled = true
    }
  }, [isVisible, path])
  
  // Extract filename and extension
  const filename = path.split('/').pop() || path
  const extension = filename.split('.').pop()?.toUpperCase() || 'FILE'
  
  return (
    <div 
      ref={cardRef}
      className="thumbnail-card"
      onClick={onClick}
      title={path}
    >
      <div className="thumbnail-preview">
        {loading && (
          <div className="thumbnail-loading">
            <div className="loading-spinner"></div>
          </div>
        )}
        {error && (
          <div className="thumbnail-error">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="11" x2="12" y2="17" />
              <line x1="9" y1="14" x2="15" y2="14" />
            </svg>
            <span className="error-text">Preview unavailable</span>
          </div>
        )}
        {thumbnail && !loading && (
          <img 
            src={thumbnail} 
            alt={filename}
            className="thumbnail-image"
          />
        )}
      </div>
      <div className="thumbnail-info">
        <span className="thumbnail-filename">{filename}</span>
        <span className="thumbnail-extension">{extension}</span>
      </div>
    </div>
  )
}

